import type { Command } from '../engine/simulation/commands';
import type { Simulation } from '../engine/simulation/simulation';

/**
 * Abstracts where commands go and when the simulation may advance.
 * Single player schedules locally; multiplayer uses deterministic lockstep.
 */
/** One player's connection, as shown in the multiplayer connection panel. */
export interface NetPlayerRow {
  player: number;
  name: string;
  color: string;
  you: boolean;
  /** Round trip to them in ms (null for yourself, or when linked only through the host). */
  pingMs: number | null;
  /** Their frame rate, as they last reported it (null if not heard yet). */
  fps: number | null;
  /** How far their game is behind ours, in seconds (null if unknown). */
  behindSec: number | null;
  /** Time the game waited for them in the last 30 seconds, in ms. */
  heldUpMs: number;
  status: 'good' | 'slow' | 'lagging' | 'left';
}

/** A lost connection being retried, as shown in the reconnection panel. */
export interface ReconnectView {
  lost: { player: number; name: string; color: string; seconds: number }[];
  /**
   * reconnecting: within the current 30 s cycle; ask: the cycle is over and you decide whether to
   * keep waiting; waiting: the cycle is over and another player (`decider`) decides.
   */
  phase: 'reconnecting' | 'ask' | 'waiting';
  decider: string;
  /** Seconds left in the current cycle. */
  cycleLeft: number;
}

export interface CommandTransport {
  readonly localPlayer: number;
  /** True for real-time networked sessions (no pause or speed changes). */
  readonly networked: boolean;
  attach(sim: Simulation): void;
  submit(cmd: Command): void;
  /** May the simulation execute the given tick now? */
  canAdvance(tick: number): boolean;
  /** Called after each executed tick. */
  afterTick(tick: number): void;
  /** Human-readable network status, or null when local. */
  status(): string | null;
  /** Desync description, if detected. */
  desync(): string | null;
  /** Per-player connection details for networked games (see NetPlayerRow). */
  players?(): NetPlayerRow[];
  /** Our own frame rate, shared with the other players. */
  setLocalFps?(fps: number): void;
  /** Lost connections being retried (networked games), or null. */
  reconnect?(): ReconnectView | null;
  /** The deciding player's answers: wait another cycle, or carry on without the lost players. */
  keepWaiting?(): void;
  continueWithout?(): void;
  dispose(): void;
}

/** Local (single-player) transport: commands execute on the next tick. */
export class LocalTransport implements CommandTransport {
  readonly networked = false;
  private sim: Simulation | null = null;
  private seq = 0;
  constructor(readonly localPlayer: number) {}
  attach(sim: Simulation): void {
    this.sim = sim;
    this.seq = Math.max(this.seq, sim.world.players[this.localPlayer]?.lastSeq + 1 || 0);
  }
  submit(cmd: Command): void {
    if (!this.sim) return;
    this.sim.schedule({ tick: this.sim.tick + 1, player: this.localPlayer, seq: this.seq++, cmd });
  }
  canAdvance(): boolean {
    return true;
  }
  afterTick(): void {}
  status(): string | null {
    return null;
  }
  desync(): string | null {
    return null;
  }
  dispose(): void {}
}

/** Replay playback: no local input, commands come from the recording. */
export class ReplayTransport implements CommandTransport {
  readonly localPlayer = -1;
  readonly networked = false;
  attach(): void {}
  submit(): void {}
  canAdvance(): boolean {
    return true;
  }
  afterTick(): void {}
  status(): string | null {
    return null;
  }
  desync(): string | null {
    return null;
  }
  dispose(): void {}
}
